import { Flex, Layout, Spin } from "antd"
import React, { useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { Outlet } from "react-router-dom"

import { MAIN_CONTENT_PADDING_CLASS } from "../../components/main-content.ts"
import { type RootState } from "../../store"
import { MinimizedWizardDock } from "../Esign/components/MinimizedWizardDock.tsx"
import { GuideOnboardingAssistantProvider } from "../Guide/assistant/GuideOnboardingAssistantContext.tsx"
import { GuideSetupAssistantPanel } from "../Guide/assistant/GuideSetupAssistantPanel.tsx"
import { GuideTourProvider } from "../Guide/tour/GuideTourContext.tsx"
import { GuideTourOverlay } from "../Guide/tour/GuideTourOverlay.tsx"
import { ProductTourProvider } from "../Guide/tour/ProductTourContext.tsx"
import { ProductTourOverlay } from "../Guide/tour/ProductTourOverlay.tsx"
import { Header } from "../Header"
import MessagingController from "../Messages/MessagingController.tsx"
import { SidebarController } from "../Sidebar/SidebarController.tsx"

const { Content } = Layout

function CustomLayout({
  auth,
  userInfo,
  isAdmin,
  isLogoutPending = false
}: ConnectedProps<typeof connector>) {
  const isAuthUserPages = !!auth?.accessToken && !!userInfo && !isAdmin

  return (
    <Spin size="large" spinning={isLogoutPending}>
      {/* `h-svh` + `overflow-hidden` bounds the whole shell to the viewport
          exactly once, so the page itself never scrolls — Header (sticky)
          and Sidebar (its own `h-[calc(100vh-header-height)]`) were already
          built assuming this; `#main-content` was the missing piece, so the
          window ended up scrolling instead and content past the viewport
          was simply unreachable. */}
      <GuideTourProvider>
        <ProductTourProvider>
          <GuideOnboardingAssistantProvider>
            <Layout
              id="global-wrap"
              className="h-svh flex flex-col overflow-hidden"
            >
              <Header />
              {/* The app's one messaging socket. It lives here rather than on
                  the Messages page because the navigation's unread badge has
                  to stay live wherever the user is. */}
              {isAuthUserPages && <MessagingController />}
              {/* min-h-0: a flex item's default `min-height: auto` lets it grow
                  past its parent's bounded height instead of respecting `flex-1`. */}
              <Flex flex={1} className="min-h-0">
                <SidebarController />
                {/* min-w-0: flex items default to min-width:auto, which lets a wide table stretch the shell.
                    flex-1 min-h-0 overflow-y-auto: this is now the one scrollable
                    region for page content, matching what Header/Sidebar already
                    assumed existed. A page wanting its own finer-grained internal
                    scrolling (e.g. Calendar's time grid) still works — it just
                    scrolls within a box that finally has a real, bounded height to
                    measure itself against. */}
                <Content
                  id="main-content"
                  className={`${MAIN_CONTENT_PADDING_CLASS} min-w-0 max-w-screen flex-1 min-h-0 overflow-y-auto`}
                >
                  <Outlet context={{ isAuthUserPages }} />
                </Content>
                <GuideSetupAssistantPanel />
              </Flex>
            </Layout>
            <GuideTourOverlay />
            <ProductTourOverlay />
            <MinimizedWizardDock />
          </GuideOnboardingAssistantProvider>
        </ProductTourProvider>
      </GuideTourProvider>
    </Spin>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isLogoutPending: state.auth.isLogoutPending,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile
})

const connector = connect(mapStateToProps, null)
export default connector(CustomLayout)
